import type { BridgeConfig } from '../../custom-ui/bridge/types';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

type BridgeConfigContext = MakeBridgeContext<'bridgeConfig'>;

type BridgeConfigKeys = keyof BridgeConfig;

// Extracts the first parameter type of a BridgeConfig callback.
// Falls back to `void` for zero-argument callbacks (e.g. `emitReadyEvent`, `refresh`, `open`)
// or for non-function members (e.g. `history`, `extensionBasePath` from HistoryApiProvider).
type BridgeConfigPayload<K extends BridgeConfigKeys> =
	NonNullable<BridgeConfig[K]> extends (...args: infer Args) => any
	? Args extends [infer First, ...any[]]
	? First
	: void
	: void;

/**
 * Creates a bridge method that delegates to a single `BridgeConfig` callback.
 *
 * Contract (mirrors `createBridgeMethod` in the legacy bridge):
 *   - Callback present  → `(await callback(payload)) ?? true`
 *   - Callback absent   → `false`
 *
 * The callback is read from `bridgeConfig` at invocation time (not creation time),
 * so updates to the product's bridge prop are always reflected.
 */
const createBridgeConfigDelegate =
	<K extends BridgeConfigKeys>(
		callbackName: K,
	): TBridgeMethod<BridgeConfigContext, BridgeConfigPayload<K>> =>
		async ({ bridgeConfig }, payload) => {
			const callback = bridgeConfig[callbackName] as ((payload: unknown) => unknown) | undefined;
			if (!callback) return false;
			return (await callback(payload)) ?? true;
		};

/**
 * The subset of `BridgeConfig` keys exposed as bridge features.
 *
 * Keys excluded from this list (e.g. `spaPush`, `history`, `extensionBasePath`)
 * are used internally by other bridge methods and are not directly dispatched
 * from the app iframe.
 */
const BRIDGE_CONFIG_DELEGATE_KEYS: readonly BridgeConfigKeys[] = [
	'changeWindowTitle',
	'close',
	'closeFlag',
	'emitReadyEvent',
	'onClose',
	'onError',
	'open',
	'openModal',
	'reconcile',
	'refresh',
	'showFlag',
	'submit',
] as const;

// Per-key typed map — consumers see the correct payload type for each delegate.
export type BridgeConfigDelegates = {
	[K in (typeof BRIDGE_CONFIG_DELEGATE_KEYS)[number]]: TBridgeMethod<
		BridgeConfigContext,
		BridgeConfigPayload<K>
	>;
};

// Double cast required: Object.fromEntries returns { [k: string]: ... } which TypeScript
// cannot narrow to a keyed mapped type. The cast is sound because the keys and factory
// are both derived from BRIDGE_CONFIG_DELEGATE_KEYS.
export const bridgeConfigDelegates = Object.fromEntries(
	BRIDGE_CONFIG_DELEGATE_KEYS.map((key) => [key, createBridgeConfigDelegate(key)]),
) as unknown as BridgeConfigDelegates;
